<script>
import validationMessages from '@/components/validations'
import {required} from "vuelidate/lib/validators"
import Monitor from '@/services/Monitor'


export default {
  components: {
    validationMessages,
  },
  props: ['configData', 'configGroups', 'configKeys', 'configDefinitions'],
  data() {
    return {
      showModal: false,
      tryingToSubmit: false,
      submitted: false,
      key: '',
      group: '',
      description: '',
      keyTypes: ['string','integer','boolean','json'],
      type: 'string',
      tenants: [],
      tenantRows: [
        {tenant: '', value: ''}
      ],
      csrf_token: localStorage.getItem('csrf_token')
    };
  },

  validations: {
    key: {required},
    tenantRows: {
      $each: {
        tenant: {required}
      }
    }
  },

  methods: {

    shownModal(){
      this.key = this.configData.key
      this.group = this.configData.group
      this.type = this.configData.type
      this.description = this.configData.description
      this.tenants = [this.configData.tenant]
      this.tenantRows[0].tenant = this.configData.tenant
      this.tenantRows[0].value = this.configData.value
    },

    refreshData() {
      this.$emit('onRefresh')
    },

    closeModal() {
      this.showModal = false
    },

    resetProps() {
      this.group = ''
      this.key = ''
      this.description = ''
      this.tenantRows = [{tenant: '', value: ''}]
      this.submitted = false
    },

    normalizeFlag(value) {
      return value === true || value === 1 || value === '1'
    },

    flagLabel(value) {
      return this.normalizeFlag(value) ? 'Yes' : 'No'
    },

    async editTenantEnvConfig() {
      this.submitted = true
      this.tryingToSubmit = true

      this.$v.$touch()
      if (this.$v.$invalid) {
        this.tryingToSubmit = false
        return
      }

      const formData = {
        group: this.group,
        key: this.key,
        type: this.type,
        description: this.description,
        values: this.tenantRows.map(r => ({
          key: this.key,
          tenant: r.tenant,
          value: r.value
        }))
      }

      try {
        const response = await Monitor.editTenantEnvConfig(formData)
        if (response.data.data) {
          this.successmsg('Environment Config Changed!')
          this.refreshData()
          this.closeModal()
        } else {
          this.failedmsg(response.data.error || 'Failed to change Environment Config!')
        }
      } catch (error) {
        const resData = error.response?.data?.error || 'Failed'
        const errorMsg = Array.isArray(resData) ? resData[0] : resData
        this.failedmsg(errorMsg)
      } finally {
        this.tryingToSubmit = false
      }
    },
  },
  watch: {
    key(newVal) {
      if (newVal) {
        // Replace spaces and non-alphanumeric chars with underscore, uppercase
        this.key = newVal
            .trim()                          // remove leading/trailing spaces
            .replace(/[^a-zA-Z0-9]/g, '_')  // replace non-alphanumeric with _
            .replace(/_+/g, '_')             // collapse multiple underscores
            .toUpperCase();                  // convert to uppercase
      }
    }
  },
  computed: {
    keyExists() {
      if (!this.key) return false;
      return this.configKeys.includes(this.key);
    },
    selectedKeyDefinition() {
      if (!this.key || !this.configDefinitions) return null;

      return this.configDefinitions.find(
          item => item.key === this.key
      ) || null;
    },
    displayedKeyDefinition() {
      return this.selectedKeyDefinition || this.configData || null
    }
  }
};
</script>

<template>
  <b-modal @shown="shownModal()" id="edit_env_config" size="lg" v-model="showModal" @hidden="resetProps" title="Edit Environment Config"
           title-class="font-18">
    <form @submit.prevent="editTenantEnvConfig">
      <div class="row">

        <div class="col-12">
          <h6 class="text-uppercase mb-2">Key Definition</h6>
          <small class="text-muted d-block">
            Information for key definition.
          </small>
        </div>

        <div class="col-12">
          <div class="mb-3">
            <label class="control-label form-label">Key Name *</label>
            <b-form-input
                v-model="key"
                :class="{ 'is-invalid': submitted && $v.key.$error }"
                :disabled="true"
                list="groupKeyOptions"
            >
            </b-form-input>
            <datalist id="groupKeyOptions">
              <option v-for="g in configKeys" :key="g" :value="g">{{ g }}</option>
            </datalist>
            <validationMessages v-if="submitted" :fieldName="'Key Name'" :validationName="$v.key"></validationMessages>
          </div>

          <div v-if="displayedKeyDefinition && keyExists" class="mb-3">
            <p class="mb-1">
              <strong>Type:</strong>
              <span>{{ displayedKeyDefinition.type }}</span>
            </p>

            <p class="mb-1 d-flex flex-wrap gap-3">
              <span><strong>Tenant Required:</strong> {{ flagLabel(displayedKeyDefinition.tenant_required) }}</span>
              <span><strong>Common Required:</strong> {{ flagLabel(displayedKeyDefinition.common_required) }}</span>
              <span><strong>Is Secret:</strong> {{ flagLabel(displayedKeyDefinition.is_secret) }}</span>
            </p>

            <p class="mb-0">
              <strong>Description:</strong>
              {{ displayedKeyDefinition.description }}
            </p>
          </div>

          <!-- Config Group -->
          <div v-if="!keyExists" class="row">
            <div class="col-9">
              <div class="mb-3">
                <label class="control-label form-label">Config Group (optional)</label>
                <input
                    v-model="group"
                    class="form-control"
                    list="groupOptions"
                    placeholder="Select or type a group"
                >
                <datalist id="groupOptions">
                  <option v-for="g in configGroups" :key="g" :value="g">{{ g }}</option>
                </datalist>
              </div>
            </div>

            <div class="col-3">
              <div class="mb-3">
                <label class="form-label">Key Type</label>
                <b-form-select
                    v-model="type"
                    :options="keyTypes"
                    class="w-100"
                ></b-form-select>
              </div>
            </div>
          </div>

          <div v-if="!keyExists" class="mb-3">
            <label class="control-label form-label">Key Description</label>
            <b-form-input v-model="description"></b-form-input>
          </div>

          <div class="row">
            <div class="col-12">
              <h6 class="text-uppercase mb-2">Tenant Values</h6>
              <small class="text-muted d-block">
                Define value for configuration key.
              </small>
            </div>
          </div>

          <div v-for="(row, index) in tenantRows" :key="index" class="mb-2 row align-items-center">

            <div class="col-2">
              <label class="form-label">Tenant</label>
              <b-form-select
                  v-model="row.tenant"
                  :options="tenants"
                  class="w-100"
                  :class="{ 'is-invalid': submitted && $v.tenantRows.$each && $v.tenantRows.$each[index].tenant.$error }"
              ></b-form-select>

              <validationMessages
                  v-if="submitted && $v.tenantRows.$each && $v.tenantRows.$each[index].tenant.$error"
                  :fieldName="'Tenant'"
                  :validationName="$v.tenantRows.$each[index].tenant"
              ></validationMessages>
            </div>

            <div class="col-9">
              <label class="form-label">Value</label>
              <b-form-input
                  v-model="row.value"
                  placeholder="Enter value"
              ></b-form-input>
            </div>

          </div>

        </div>
      </div>
    </form>

    <template v-slot:modal-footer>
      <b-button variant="light" @click="closeModal">Close</b-button>
      <b-button variant="primary" @click="editTenantEnvConfig" :disabled="tryingToSubmit">
        <b-spinner v-show="tryingToSubmit" small></b-spinner>
        Submit
      </b-button>
    </template>
  </b-modal>
</template>
